<template>
    <ol class="flex flex-col gap-2">
        <li v-for="step in steps" :key="step.number" class="flex gap-2">
            <span
                class="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-medium"
                :class="badge(step)"
            >{{ step.number }}</span>

            <div class="min-w-0 flex-1">
                <AssistantOperationCard
                    v-if="step.operation"
                    :operation="step.operation"
                    :actionable="ownThread && actionable(step)"
                    :deciding="deciding === step.operation.id"
                    :class="{ 'opacity-60': !actionable(step) && step.operation.status === 'proposed' }"
                    @decide="confirm => emit('decide', step, confirm)"
                />

                <!-- A step whose proposal cannot be built until the one before it has
                     run. It is greyed and has no buttons, because there is genuinely
                     nothing to press: the API would refuse to close a room somebody is
                     still sleeping in. It is on the list so the desk can see the whole
                     plan before agreeing to any of it. -->
                <div v-else class="rounded-lg border-2 border-dashed border-accented p-3 text-sm opacity-60">
                    <div class="flex items-center gap-2 font-medium text-muted">
                        <UIcon name="i-lucide-clock" class="size-4 shrink-0" />
                        {{ step.title }}
                    </div>

                    <p v-if="step.note" class="mt-1 text-xs text-muted">{{ step.note }}</p>
                </div>

                <p v-if="waitingOn(step)" class="mt-1 flex items-center gap-1 text-xs text-muted">
                    <UIcon name="i-lucide-arrow-up" class="size-3 shrink-0" />
                    {{ $t('assistant.waits_for_step', { step: step.depends_on }) }}
                </p>

                <!-- Its turn has come and it has no proposal of its own, so one is being
                     asked for. Said here rather than left as a box that never changes:
                     the card arrives in the assistant's next answer, just below. -->
                <p v-else-if="!step.operation" class="mt-1 flex items-center gap-1 text-xs text-muted">
                    <UIcon name="i-lucide-arrow-down" class="size-3 shrink-0" />
                    {{ $t('assistant.step_prepared_below') }}
                </p>
            </div>
        </li>
    </ol>
</template>

<script setup lang="ts">
    import AssistantOperationCard from '@/components/AssistantOperationCard.vue'
    import type { IChatStep } from '@/types/Chat'

    /**
     * A multi-step answer, as a numbered checklist.
     *
     * The rule it draws is one sentence: a step may be decided when the step it depends
     * on has been EXECUTED. Everything else — the greyed cards, the "waits for step 1"
     * line, the dashed box for a step with no proposal yet — is that rule made visible,
     * so that nobody presses the second half of a plan before the first half has
     * happened.
     *
     * Human confirmation stays per step (the house rule: the model prepares, a person
     * decides). What the plan removes is the retyping between them.
     */

    const props = defineProps<{
        steps:     IChatStep[]
        ownThread: boolean
        /** Id of the operation currently being confirmed or declined. */
        deciding:  string
    }>()

    const emit = defineEmits<{ decide: [step: IChatStep, confirm: boolean] }>()

    const stepByNumber = (number: number): IChatStep | undefined =>
        props.steps.find(one => one.number === number)

    /** A step may be pressed when nothing precedes it, or when what precedes it ran. */
    const actionable = (step: IChatStep): boolean => {
        if (!step.depends_on) return true

        return stepByNumber(step.depends_on)?.operation?.status === 'executed'
    }

    const waitingOn = (step: IChatStep): boolean =>
        !actionable(step) && step.operation?.status !== 'declined' && step.operation?.status !== 'failed'

    const badge = (step: IChatStep): string => {
        if (step.operation?.status === 'executed') return 'bg-success/15 text-success'
        if (step.operation?.status === 'failed') return 'bg-error/15 text-error'
        if (actionable(step) && step.operation) return 'bg-primary/15 text-primary'

        return 'bg-elevated text-muted'
    }
</script>
